import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../Components/ui/dataTable";
import { Button } from "../../Components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../Components/ui/popover";
import { SkeletonTable } from "../../Components/ui/skeleton";
import Pagination from "../../Components/ui/pagination";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../Components/ui/tabs";
import { ProtectedRoutes } from "../../config/protectedRoutes";
import { useDebouncedValue } from "../../Lib/hooks";
import toast from "react-hot-toast";
import { useGetAllZonesQuery, useDeleteZoneMutation } from "./ZoneApi";
import { colorForIndex } from "./utils/colors";
import { areaUniquenessKey, uniqueAreas } from "./utils/areas";
import ZonePricingTab from "./components/ZonePricingTab";

const areasPreview = (areas = []) => {
  if (!areas.length) return "No areas yet";
  const names = areas.slice(0, 2).map((a) => a.name);
  const rest = areas.length - names.length;
  return rest > 0 ? `${names.join(", ")} +${rest}` : names.join(", ");
};

const AreasCell = ({ areas = [] }) => {
  const [open, setOpen] = useState(false);
  const visibleAreas = uniqueAreas(areas);

  const chip = (
    <div
      className='inline-flex items-center gap-2 bg-[#F7F7F5] border border-lightGray rounded-full px-3 py-1 text-sm text-HeaderColor whitespace-nowrap cursor-default'
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        className='w-2 h-2 rounded-full'
        style={{ background: visibleAreas[0] ? colorForIndex(0) : "#E0E0E0" }}
      />
      {areasPreview(visibleAreas)}
    </div>
  );

  if (!visibleAreas.length) return chip;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{chip}</PopoverTrigger>
      <PopoverContent
        className='w-64 p-3'
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <p className='text-xs font-semibold text-HeaderColor mb-2'>
          Areas in this zone ({visibleAreas.length})
        </p>
        <ul className='space-y-1.5 max-h-56 overflow-y-auto'>
          {visibleAreas.map((area, index) => (
            <li
              key={`${areaUniquenessKey(area)}_${index}`}
              className='flex items-center gap-2 text-sm text-primaryBlack'
            >
              <span
                className='w-1.5 h-1.5 rounded-full flex-shrink-0'
                style={{ background: colorForIndex(index) }}
              />
              {area.name}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

const Zones = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("areas");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebouncedValue(search, 500);

  const { data, isLoading: loading } = useGetAllZonesQuery(
    { page, limit: 10, search: debouncedSearch },
    {
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    },
  );

  const [deleteZone] = useDeleteZoneMutation();

  const handleDelete = async (zone) => {
    if (!window.confirm(`Delete "${zone.name}"? This cannot be undone.`))
      return;
    try {
      const data = await deleteZone(zone.id).unwrap();
      if (data?.statusCode < 300) {
        toast.success("Zone deleted", { id: "zoneDelete" });
      }
    } catch (error) {
      toast.error("Could not delete zone", { id: "zoneDelete" });
    }
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Zone",
      cell: ({ row }) => (
        <div className='font-semibold whitespace-nowrap'>
          {row.original.number ? `Zone ${row.original.number} — ` : ""}
          {row.getValue("name")}
        </div>
      ),
    },
    {
      id: "areas",
      header: "Areas",
      cell: ({ row }) => <AreasCell areas={row.original.areas || []} />,
    },
    {
      id: "action",
      cell: ({ row }) => {
        const zone = row.original;
        return (
          <div className='flex gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => navigate(`/zones/${zone.id}/edit`)}
            >
              Edit
            </Button>
            <Button
              variant='failed'
              size='sm'
              onClick={() => handleDelete(zone)}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className='bg-white p-4'>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className='flex items-start justify-between mb-6'>
          <div>
            <h2 className='text-2xl font-bold text-primaryBlack'>Zones</h2>
            <p className='text-sm text-[#667499]'>
              Group Calabar areas into zones for riders, vendors and customers.
            </p>
          </div>
          {activeTab === "areas" && (
            <div className='flex gap-2'>
              <Button
                variant='outline'
                onClick={() => navigate("/zones/overview")}
              >
                View All-Zones Map
              </Button>
              <Button onClick={() => navigate("/zones/new")}>+ Add Zone</Button>
            </div>
          )}
        </div>

        <TabsList className='mb-4'>
          <TabsTrigger value='areas'>Zone Areas</TabsTrigger>
          <TabsTrigger value='pricing'>Zone Pricing</TabsTrigger>
        </TabsList>

        <TabsContent value='areas'>
          <div className='flex flex-wrap items-center gap-3 mb-4'>
            <div className='flex items-center bg-[#F5FEF1] w-full sm:w-[240px] p-1 rounded-sm border border-gray-100'>
              <input
                className='bg-transparent w-full px-1 outline-none placeholder:text-sm text-sm'
                value={search}
                placeholder='Search zones or areas'
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className='table-scroll max-h-[70vh] overflow-auto max-w-full rounded-md border'>
            <div className='w-full'>
              {loading ? (
                <SkeletonTable />
              ) : (
                <DataTable columns={columns} data={data?.data} />
              )}
            </div>
          </div>

          {!loading && (
            <Pagination
              page={page}
              limit={data?.limit ?? 0}
              total={data?.total ?? 0}
              setPage={setPage}
            />
          )}
        </TabsContent>

        <TabsContent value='pricing'>
          <ZonePricingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProtectedRoutes(Zones, [1, 2]);
